/**
 * Agreements, from the office.
 *
 * Two screens in one file, because they are one job: a list whose only real question is *who has
 * not signed yet*, and a page for one agreement that shows the document, the signatures on it, and
 * the one control that matters at each point in its life.
 *
 * Three decisions are worth knowing before changing anything here.
 *
 * **The signing state is the headline, not the status.** "Out for signature" tells a manager
 * nothing they can act on; "1 of 3 signed — waiting on Efe Oghenekaro" tells them who to ring. So
 * the count leads every row and the outstanding names lead the page, and the status badge sits
 * beside them rather than in front of them.
 *
 * **Nothing on these screens can change what an agreement says.** There is no edit form here at
 * all, and that is the feature. Once an agreement has been sent its terms are frozen — see
 * `packages/core/src/agreements.ts` — so the repair for a document that no longer matches the
 * tenancy is `Raise a replacement`, which generates a fresh one from the tenancy as it now stands
 * and supersedes the old one when it goes out.
 *
 * **The divergence panel is the honest half of that.** Where the tenancy has moved since the
 * agreement froze it, the page says so, field by field, with both figures — because the alternative
 * is an agency believing a signed document describes a letting it no longer describes.
 */

import { useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AGREEMENT_STATUSES, PAPER_SIGNATURE_STATEMENT, agreementStatusInfo } from "@rentorng/core";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { useQueryClient } from "@tanstack/react-query";
import { useFormat } from "@/lib/format";
import { usePageTitle } from "@/lib/title";
import { useSession } from "@/lib/session";
import { useListParams, type PageMeta } from "@/lib/list-params";
import { Pagination, SearchBox } from "@/components/list-toolbar";
import { Refusal } from "@/components/padlock";
import { SearchSelect } from "@/components/search-select";
import { resolveParty, searchParties } from "@/lib/pickers";
import { IconSignature } from "@/components/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  Checkbox,
  Detail,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  Table,
} from "@/components/ui";

/* ------------------------------------------------------------------- shapes */

interface SigningCounts {
  total: number;
  signed: number;
  outstanding: number;
  complete: boolean;
  summary: string;
  /**
   * How the signatures given were given.
   *
   * `summary` says "3 of 3 signed" whether this product watched three people sign or is holding
   * three photographs somebody emailed in. These two are what stop the screen from saying the same
   * thing about two quite different documents — see ADR-0126 and the note on `SIGNATURE_KINDS`.
   */
  witnessed: number;
  onPaper: number;
  anyOnPaper: boolean;
  paperSummary: string | null;
}

interface AgreementRow {
  id: string;
  reference: string;
  status: string;
  issuedOn: string;
  sentAt: string | null;
  completedAt: string | null;
  leaseId: string;
  leaseReference: string | null;
  propertyName: string | null;
  unitLabel: string | null;
  tenantName: string | null;
  signing: SigningCounts;
}

interface ListResponse {
  agreements: AgreementRow[];
  page: PageMeta;
  statuses: typeof AGREEMENT_STATUSES;
}

interface SignatoryRow {
  id: string;
  partyId: string | null;
  role: string;
  roleLabel: string;
  name: string;
  email: string | null;
  phone: string | null;
  signedAt: string | null;
  signedName: string | null;
  signedIp: string | null;
  signedUserAgent: string | null;
  signedDigest: string | null;
  signedThisDocument: boolean | null;
  /** `typed` or `on_paper`, and null where nobody has signed this block yet. */
  signatureKind: string | null;
  signatureKindLabel: string | null;
  /** Whether this product observed the signing. Null where there is no signature to describe. */
  witnessed: boolean | null;
  paper: {
    documentId: string | null;
    signedOn: string | null;
    source: string | null;
    sourceLabel: string;
    recordedAt: string | null;
    recordedBy: string | null;
    held: boolean;
  } | null;
}

interface AgreementDetail {
  id: string;
  reference: string;
  status: string;
  leaseId: string;
  leaseReference: string;
  propertyName: string;
  unitLabel: string;
  issuedOn: string;
  sentAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancelledReason: string | null;
  supersedesId: string | null;
  digest: string;
  fingerprint: string;
  stampedDigest: string | null;
  altered: boolean;
  frozen: boolean;
  openForSignature: boolean;
  leaseMissing: boolean;
  signing: SigningCounts & { waitingOn: string[] };
  divergence: Array<{ field: string; label: string; agreed: string; now: string }>;
  parties: SignatoryRow[];
  document: {
    title: string;
    subject: string;
    issuedOnLabel: string;
    recitals: string[];
    particulars: Array<[string, string]>;
    schedule: Array<{ label: string; when: string; amountLabel: string }>;
    totals: { commencementLabel: string; perPeriodLabel: string; periodSuffix: string };
    conditions: string[];
    notes: string | null;
    signatureStatement: string;
    supersedes: string | null;
  };
}

/**
 * The badge every screen uses for an agreement's state.
 *
 * The tone comes from `AGREEMENT_STATUSES` in core rather than from a table here, so a state added
 * there arrives with its own colour instead of falling through to grey on the day somebody forgets
 * this file exists.
 */
function StatusBadge({ status }: { status: string }) {
  const info = agreementStatusInfo(status);
  const tone = info.tone as "muted" | "info" | "success" | "warn";
  return <Badge tone={tone}>{info.label}</Badge>;
}

/**
 * "1 of 3 signed", with the bar that makes it readable across a column of rows.
 *
 * **The count and the kind are drawn as two things because they are two things.** "3 of 3 signed"
 * is the same sentence whether every party typed their name here or one of them sent in a
 * photograph of a page, and an agency scanning a column of agreements is entitled to see which is
 * which without opening any of them. So where a signature is an uploaded paper copy the row says
 * so, beside the count and never folded into it.
 */
function SigningPill({ signing }: { signing: SigningCounts }) {
  const done = signing.total > 0 && signing.complete;
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span
        aria-hidden
        className="inline-flex h-1.5 w-16 overflow-hidden rounded-full bg-ink-200"
        title={signing.summary}
      >
        <span
          className={done ? "h-full bg-emerald-500" : "h-full bg-brand-500"}
          style={{ width: `${signing.total ? (signing.signed / signing.total) * 100 : 0}%` }}
        />
      </span>
      <span className={done ? "text-sm font-medium text-emerald-700" : "text-sm text-ink-600"}>{signing.summary}</span>
      {signing.anyOnPaper ? (
        <Badge tone="info">
          <span data-testid="signing-on-paper">{signing.onPaper} on paper</span>
        </Badge>
      ) : null}
    </span>
  );
}

/**
 * How one block was signed, in the width of a table cell.
 *
 * Never "signed" on its own. A typed signature and an uploaded copy are two different claims and
 * this is the control that keeps them apart on the screen an agency actually looks at.
 */
function SignatureKindBadge({ party }: { party: SignatoryRow }) {
  if (!party.signedAt) return <span className="text-ink-500">—</span>;
  if (party.witnessed) {
    return (
      <Badge tone="success">
        <span data-testid="kind-typed">Typed here</span>
      </Badge>
    );
  }
  return (
    <Badge tone="info">
      <span data-testid="kind-on-paper">On paper</span>
    </Badge>
  );
}

/**
 * Recording a signed paper copy, for one party, from the office.
 *
 * **Everything about this control is written to stop it looking like signing.** It is not a name
 * box; it is a file, a day, and a sentence saying what will be recorded. The button says "Record
 * the uploaded copy" rather than "Sign", the hint says the agency is stating something on somebody
 * else's behalf, and the confirmation the agency ticks is about *that* rather than about the terms.
 *
 * The digest the screen rendered goes up with the file, exactly as it does with a typed name. A
 * scan is asserted to be a copy of some wording, and a page printed before the document moved is
 * not a copy of the document as it now stands.
 *
 * Sent by hand rather than through `api.post`, because a scan is multipart: base64 in a JSON body
 * would make a five megabyte photograph a third bigger on the connection that is already the slow
 * part. Same reasoning as the sale-papers uploader on `PurchaseDetail`.
 */
function RecordPaperCopy({
  agreementId,
  party,
  digest,
  issuedOn,
  today,
}: {
  agreementId: string;
  party: SignatoryRow;
  digest: string;
  issuedOn: string;
  today: string;
}) {
  /* The browser's idea of today can be a day behind the agency's — the agreement is dated in the
     agency's own timezone — so the box opens on whichever is later rather than on a value the
     server would refuse the moment it was submitted. */
  const opensOn = today > issuedOn ? today : issuedOn;
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const file = fileInput.current?.files?.[0];
    if (!file) {
      setProblem("Choose the signed copy to upload.");
      return;
    }

    const fields = new FormData(event.currentTarget);
    const form = new FormData();
    form.append("file", file);
    form.append("signedOn", String(fields.get("signedOn") ?? ""));
    form.append("digest", digest);

    setBusy(true);
    setProblem(null);
    try {
      const response = await fetch(`/api/v1/agreements/${agreementId}/parties/${party.id}/paper`, {
        method: "POST",
        body: form,
        credentials: "include",
      });
      if (response.ok) {
        await client.invalidateQueries({ queryKey: ["agreement", agreementId] });
        await client.invalidateQueries({ queryKey: ["agreements"] });
        setOpen(false);
        setConfirmed(false);
      } else {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        setProblem(payload?.error ?? "That copy could not be recorded.");
      }
    } catch {
      setProblem("Could not reach the server. Check your connection and try again.");
    }
    setBusy(false);
  }

  if (!open) {
    return (
      <Button variant="secondary" onClick={() => setOpen(true)} data-testid={`record-paper-${party.role}`}>
        Record a paper copy
      </Button>
    );
  }

  return (
    <form onSubmit={submit} className="mt-2">
      {problem ? <Alert tone="danger">{problem}</Alert> : null}
      <p className="mb-3 text-sm leading-relaxed text-ink-600">
        For <strong>{party.name}</strong>, who signed a printed copy by hand. This is recorded as an uploaded copy
        <em> asserted</em> to be signed — not as a signature given here. Nothing in the file is compared with this
        document's fingerprint, and the page will say so wherever this signature appears.
      </p>
      <Field label="The signed copy" htmlFor={`paper-file-${party.id}`} hint="A PDF or a photograph, up to 15MB.">
        <input
          id={`paper-file-${party.id}`}
          ref={fileInput}
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp"
          className="block w-full text-sm"
          required
        />
      </Field>
      <div className="mt-3">
        <Field
          label="The day it was signed"
          htmlFor={`paper-day-${party.id}`}
          hint="As written on the paper. Recorded as what the copy states rather than as something observed here."
        >
          <Input id={`paper-day-${party.id}`} name="signedOn" type="date" min={issuedOn} defaultValue={opensOn} required />
        </Field>
      </div>
      <div className="mt-3">
        <Checkbox
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
          /* Named, so a suite can press *this* box rather than "the last checkbox on the page" —
             which is what it had to do, and which timed out the moment a second one appeared
             further down the screen. `Checkbox` spreads its rest props onto the input itself. */
          data-testid={`record-paper-confirm-${party.role}`}
          label={`I have this copy and it is ${party.name}'s signature on this agreement`}
          hint="You are recording a statement on their behalf. It cannot be undone once every party has signed."
        />
      </div>
      <div className="mt-4 flex gap-2">
        <Button type="submit" disabled={!confirmed || busy} data-testid={`record-paper-submit-${party.role}`}>
          {busy ? "Recording…" : "Record the uploaded copy"}
        </Button>
        <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/* --------------------------------------------------------------------- list */

export function AgreementsPage() {
  const fmt = useFormat();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const outstanding = params.get("outstanding") === "1";
  const list = useListParams();

  const setFilter = (next: { status?: string; outstanding?: boolean }) =>
    setParams(
      (current) => {
        const query = new URLSearchParams(current);
        if (next.status) query.set("status", next.status);
        else query.delete("status");
        if (next.outstanding) query.set("outstanding", "1");
        else query.delete("outstanding");
        query.delete("page"); // a new filter starts at the beginning
        return query;
      },
      { replace: true },
    );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["agreements", status, outstanding, list.q, list.page],
    queryFn: () =>
      api.get<ListResponse>("/agreements", {
        status,
        outstanding: outstanding ? "1" : "",
        q: list.q,
        page: list.page,
      }),
    placeholderData: (previous) => previous,
  });

  const rows = data?.agreements ?? [];
  const waiting = rows.filter((row) => row.status === "sent" && !row.signing.complete).length;

  return (
    <div>
      <PageHeader
        title="Agreements"
        subtitle={`${rows.length} shown${waiting ? ` · ${waiting} awaiting a signature` : ""}`}
        description="Every agreement is generated from the tenancy it covers, so it cannot disagree with the record it came from. Once it has been sent, its terms are fixed: a change is a new agreement, never an edit to one somebody has already signed."
      />

      <Card className="mb-5">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="State" htmlFor="status">
            <Select id="status" value={status} onChange={(event) => setFilter({ status: event.target.value, outstanding })}>
              <option value="">Every state</option>
              {AGREEMENT_STATUSES.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </Select>
          </Field>
          <Button
            variant={outstanding ? "primary" : "secondary"}
            onClick={() => setFilter({ status, outstanding: !outstanding })}
          >
            Awaiting a signature
          </Button>
          <div className="ml-auto w-full sm:w-64">
            <SearchBox params={list} placeholder="Search a reference, a property or a unit…" />
          </div>
        </div>
      </Card>

      {isLoading ? <Spinner /> : null}
      {error ? <ErrorState error={error} retry={refetch} /> : null}

      {!isLoading && !error && rows.length === 0 ? (
        <EmptyState
          title="No agreements yet"
          hint="An agreement is raised from a tenancy. Open one and press “Raise an agreement” — every figure comes from the tenancy itself, so there is nothing to retype."
          action={
            <Link className="btn btn-secondary" to="/app/tenancies">
              Go to tenancies
            </Link>
          }
        />
      ) : null}

      {rows.length ? (
        <Card>
          <Table head={<tr><th>Reference</th><th>Where</th><th>Tenant</th><th>Signed</th><th>State</th><th>Raised</th></tr>}>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <Link className="link font-medium" to={`/app/agreements/${row.id}`}>
                    {row.reference}
                  </Link>
                  <div className="text-xs text-ink-500">{row.leaseReference ?? "—"}</div>
                </td>
                <td>
                  <div>{row.propertyName ?? "—"}</div>
                  <div className="text-xs text-ink-500">{row.unitLabel ?? "—"}</div>
                </td>
                <td>{row.tenantName ?? "—"}</td>
                <td>
                  <SigningPill signing={row.signing} />
                </td>
                <td>
                  <StatusBadge status={row.status} />
                </td>
                <td>{fmt.date(row.issuedOn)}</td>
              </tr>
            ))}
          </Table>
          <Pagination meta={data?.page} params={list} noun="agreements" />
        </Card>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------- detail */

export function AgreementDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const fmt = useFormat();
  const { can } = useSession();
  const [signing, setSigning] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["agreement", id],
    queryFn: () => api.get<AgreementDetail>(`/agreements/${id}`),
  });

  usePageTitle(data ? `Agreement ${data.reference}` : "Agreement");

  const send = useApiMutation({
    mutationFn: () => api.post(`/agreements/${id}/send`, {}),
    invalidates: [["agreement", id], ["agreements"]],
  });

  const sign = useApiMutation({
    mutationFn: (input: { signedName: string; digest: string; agreed: true }) =>
      api.post(`/agreements/${id}/sign`, input),
    invalidates: [["agreement", id], ["agreements"]],
    onSuccess: () => setSigning(false),
  });

  const withdraw = useApiMutation({
    mutationFn: (input: { reason: string }) => api.post(`/agreements/${id}/cancel`, input),
    invalidates: [["agreement", id], ["agreements"]],
    onSuccess: () => setWithdrawing(false),
  });

  const discard = useApiMutation({
    mutationFn: () => api.delete(`/agreements/${id}`),
    invalidates: [["agreements"]],
    onSuccess: () => navigate("/app/agreements"),
  });

  const replace = useApiMutation({
    mutationFn: (input: { leaseId: string; supersedesId: string }) =>
      api.post<{ id: string }>("/agreements", input),
    invalidates: [["agreements"]],
    onSuccess: (created) => navigate(`/app/agreements/${created.id}`),
  });

  /*
    Taking back a paper copy that was recorded in error.

    Not "unsigning" anybody: what is withdrawn is the agency's own assertion that a file it uploaded
    is that party's signed copy. The endpoint refuses it once the agreement is complete, which is
    why the control is only drawn while it is still out for signature.
  */
  const withdrawPaper = useApiMutation({
    mutationFn: (blockId: string) => api.delete(`/agreements/${id}/parties/${blockId}/paper`),
    invalidates: [["agreement", id], ["agreements"]],
  });

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorState error={error} retry={refetch} />;

  const doc = data.document;
  const agency = data.parties.find((party) => party.role === "agency");
  const mayAct = can("document.edit");

  return (
    <div>
      <PageHeader
        title={`Agreement ${data.reference}`}
        subtitle={doc.subject}
        description="Generated from the tenancy it covers. Its terms were fixed when it was sent; every signature below records what was signed and when."
        actions={
          <div className="flex flex-wrap gap-2">
            <a className="btn btn-secondary" href={`/api/v1/documents/agreement/${data.id}.pdf?inline`} target="_blank" rel="noreferrer">
              Open the PDF
            </a>
            <a className="btn btn-secondary" href={`/api/v1/documents/agreement/${data.id}.pdf`}>
              Download
            </a>
            {mayAct && data.status === "draft" ? (
              <Button onClick={() => send.mutate(undefined as never)} disabled={send.isPending}>
                {send.isPending ? "Sending…" : "Send for signature"}
              </Button>
            ) : null}
            {mayAct && (data.status === "sent" || data.status === "signed") ? (
              <Button
                variant="secondary"
                onClick={() => replace.mutate({ leaseId: data.leaseId, supersedesId: data.id })}
                disabled={replace.isPending}
              >
                {replace.isPending ? "Raising…" : "Raise a replacement"}
              </Button>
            ) : null}
          </div>
        }
      />

      <Refusal error={send.error ?? sign.error ?? withdraw.error ?? discard.error ?? replace.error} message={send.errorMessage ?? sign.errorMessage ?? withdraw.errorMessage ?? discard.errorMessage ?? replace.errorMessage} />

      {/*
        The one thing on this page allowed to shout, and it should never appear. `altered` means the
        stored terms no longer hash to the fingerprint recorded when the agreement was issued. No
        endpoint in the product can produce it; it is here so that if anything ever does, the agency
        is told rather than left to find out by comparing two copies.
      */}
      {data.altered ? (
        <Alert tone="danger" title="This agreement no longer matches what was signed">
          The terms held for this agreement do not produce the fingerprint recorded when it was issued. Do not rely on
          this copy. Raise a replacement and tell us what happened.
        </Alert>
      ) : null}

      {/*
        Complete, and complete of what.

        "Signed by everybody" over a party this product never watched sign is the sentence this
        whole feature was written to avoid. Where one of the signatures is an uploaded copy the
        screen says so at the top, in the same breath as the good news, rather than leaving it to
        be discovered in a table further down.
      */}
      {data.signing.anyOnPaper ? (
        <Alert tone="info" title="Not every signature on this was given here">
          <span data-testid="agreement-paper-notice">
            {data.signing.paperSummary} — a file uploaded and asserted to be this agreement signed by hand. Nothing in
            it has been compared with this document's fingerprint, and this product did not record that signing as it
            happened. The copy is kept with the agreement and is linked beside the party below.
          </span>
        </Alert>
      ) : null}

      {data.status === "cancelled" ? (
        <Alert tone="warn" title="Withdrawn">
          Withdrawn on {fmt.dateTime(data.cancelledAt)}. {data.cancelledReason}
        </Alert>
      ) : null}

      {data.status === "superseded" ? (
        <Alert tone="muted" title="Replaced">
          A later agreement replaced this one. It is kept exactly as it stood, with every signature it carried.
        </Alert>
      ) : null}

      {/*
        What has moved on the tenancy since this document froze it. Computed on every read rather
        than stored — see the note in `packages/core/src/agreements.ts` about why a stored answer to
        "are these two still the same" is worse than none at all.
      */}
      {data.divergence.length ? (
        <Alert tone="warn" title="The tenancy has changed since this agreement was raised">
          <p className="mb-2">
            The agreement still says what it said, which is what the signatures are on. To agree the new terms, raise a
            replacement — this one is not edited.
          </p>
          <ul className="list-disc pl-5 text-sm">
            {data.divergence.map((entry) => (
              <li key={entry.field}>
                <strong>{entry.label}:</strong> the agreement says {entry.agreed}; the tenancy now says {entry.now}.
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
        <div className="grid gap-5">
          <Card>
            <CardTitle>Who has signed</CardTitle>
            <div className="mb-4">
              <SigningPill signing={data.signing} />
            </div>
            {data.signing.waitingOn.length ? (
              <p className="mb-4 text-sm text-ink-600">
                Waiting on <strong>{data.signing.waitingOn.join(", ")}</strong>.
              </p>
            ) : null}

            {/*
              The "How" column is not decoration and must not be dropped to save width.

              Without it this table says "signed" identically for a name somebody typed in front of
              us and for a photograph of a page that arrived by email, and the whole distinction
              this feature exists to keep would be visible in the payload and invisible on the one
              screen an agency reads. ADR-0126.
            */}
            <Table head={<tr><th>Party</th><th>Name</th><th>How</th><th>Signed</th></tr>}>
              {data.parties.map((party) => (
                <tr key={party.id}>
                  <td className="whitespace-nowrap">{party.roleLabel}</td>
                  <td>
                    <div>{party.name}</div>
                    {party.email ? <div className="text-xs text-ink-500">{party.email}</div> : null}
                  </td>
                  <td className="whitespace-nowrap">
                    <SignatureKindBadge party={party} />
                  </td>
                  <td>
                    {party.signedAt && party.witnessed ? (
                      <div>
                        <div className="font-medium">{party.signedName}</div>
                        <div className="text-xs text-ink-500">
                          {fmt.dateTime(party.signedAt)}
                          {party.signedIp ? ` · ${party.signedIp}` : ""}
                        </div>
                        {/* Only ever shown when something is wrong, which is exactly when it matters. */}
                        {party.signedThisDocument === false ? (
                          <div className="text-xs font-medium text-rose-700">Signed a different version of this document</div>
                        ) : null}
                      </div>
                    ) : party.signedAt ? (
                      /*
                        A paper block, and deliberately not dressed as a signature.

                        There is no name in bold where a typed signature would sit, because nobody
                        typed one and printing the party's own name there would manufacture the
                        appearance of one out of a row in a table. What is shown is what is actually
                        held: an uploaded copy, the day it claims, who supplied it, and a link to the
                        file itself.
                      */
                      <div>
                        <div className="text-sm text-ink-700" data-testid="paper-claim">
                          An uploaded copy, asserted to be signed
                        </div>
                        <div className="text-xs text-ink-500">
                          {party.paper?.signedOn ? `Dated ${fmt.date(party.paper.signedOn)} on the paper` : "No day recorded"}
                          {party.paper?.sourceLabel ? ` · ${party.paper.sourceLabel.toLowerCase()}` : ""}
                          {party.paper?.recordedBy ? ` (${party.paper.recordedBy})` : ""}
                          {party.paper?.recordedAt ? ` · received ${fmt.dateTime(party.paper.recordedAt)}` : ""}
                        </div>
                        {party.paper?.held && party.paper.documentId ? (
                          <a
                            className="link text-xs"
                            href={`/api/v1/agreements/${data.id}/counterparts/${party.paper.documentId}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="paper-download"
                          >
                            Open the copy that was uploaded
                          </a>
                        ) : (
                          <div className="text-xs text-ink-500">The uploaded copy is no longer held.</div>
                        )}
                        {mayAct && data.status === "sent" ? (
                          <div className="mt-1">
                            <button
                              type="button"
                              className="link text-xs"
                              onClick={() => withdrawPaper.mutate(party.id)}
                              disabled={withdrawPaper.isPending}
                              data-testid="paper-withdraw"
                            >
                              Take this back
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <span className="text-ink-500">Not yet</span>
                    )}
                  </td>
                </tr>
              ))}
            </Table>

            {mayAct && data.openForSignature && agency && !agency.signedAt && !data.altered ? (
              <div className="mt-5 border-t border-ink-200 pt-5">
                {signing ? (
                  <form
                    onSubmit={(event: FormEvent<HTMLFormElement>) => {
                      event.preventDefault();
                      const form = new FormData(event.currentTarget);
                      sign.mutate({
                        signedName: String(form.get("signedName") ?? ""),
                        /* The digest the screen actually rendered goes back with the signature. The
                           server refuses it if the document has moved, so nobody signs wording they
                           were not shown. */
                        digest: data.digest,
                        agreed: true,
                      });
                    }}
                  >
                    <Field label="Sign for the agency" htmlFor="signedName" hint="Type your own name. It is recorded with the time, this account and the document's fingerprint.">
                      <Input id="signedName" name="signedName" placeholder="Adaeze Okafor" required />
                    </Field>
                    <div className="mt-3 flex gap-2">
                      <Button type="submit" disabled={sign.isPending}>
                        {sign.isPending ? "Signing…" : "Sign"}
                      </Button>
                      <Button type="button" variant="secondary" onClick={() => setSigning(false)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                ) : (
                  <Button onClick={() => setSigning(true)}>Sign for the agency</Button>
                )}
              </div>
            ) : null}

            {/*
              Recording a paper copy, for whichever parties are still outstanding.

              **Below the agency's own signing box, and after it in the markup**, which is a
              decision rather than a layout accident. Typing a name records more and is the ordinary
              path; the paper route is the exception and reads as one. The control also deliberately
              does not use the word "sign" — partly because it is not signing, and partly because
              two controls a person reads as "sign" on one screen is a trap for them exactly as it
              was for the suite that pressed the wrong one.

              A control per outstanding party, underneath the table rather than beside each row: one
              repeated down a column of three would compete with the signing box beside it.
              `Refusal` is not used here because the upload is sent by hand rather than through the
              mutation client — the component holds its own error.
            */}
            {mayAct && data.status === "sent" && !data.altered ? (
              <div className="mt-5 border-t border-ink-200 pt-5">
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-500">
                  Somebody signed on paper
                </h3>
                <p className="mb-3 text-sm text-ink-600">
                  A party who printed this agreement and signed it by hand can send the copy back. Record it here, or
                  they can upload it themselves from their own account. It is recorded as an uploaded copy, never as a
                  signature given here.
                </p>
                <div className="grid gap-4">
                  {data.parties
                    .filter((party) => !party.signedAt)
                    .map((party) => (
                      <div key={party.id}>
                        <p className="mb-1 text-sm font-medium">
                          {party.roleLabel} · {party.name}
                        </p>
                        <RecordPaperCopy
                          agreementId={data.id}
                          party={party}
                          digest={data.digest}
                          /* The floor the endpoint enforces: nobody signed a document on a day
                             before it existed. Passed down so the box refuses it in the browser
                             rather than letting somebody fill the form in and be told afterwards. */
                          issuedOn={data.issuedOn}
                          today={new Date().toISOString().slice(0, 10)}
                        />
                      </div>
                    ))}
                  {data.parties.every((party) => party.signedAt) ? (
                    <p className="text-sm text-ink-500">Everybody has signed. There is nothing left to record.</p>
                  ) : null}
                </div>
              </div>
            ) : null}

          </Card>

          <Card>
            <CardTitle>The document</CardTitle>
            {doc.recitals.map((line, index) => (
              <p key={index} className="mb-3 text-sm leading-relaxed text-ink-800">
                {line}
              </p>
            ))}

            <h3 className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-ink-500">The particulars</h3>
            <dl className="grid gap-2 sm:grid-cols-2">
              {doc.particulars.map(([label, value]) => (
                <Detail key={label} label={label}>
                  {value}
                </Detail>
              ))}
            </dl>

            <h3 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-ink-500">What is payable</h3>
            <Table head={<tr><th>Charge</th><th>When</th><th className="num">Amount</th></tr>}>
              {doc.schedule.map((line, index) => (
                <tr key={index}>
                  <td className="font-medium">{line.label}</td>
                  <td>{line.when}</td>
                  <td className="num tabular">{line.amountLabel}</td>
                </tr>
              ))}
            </Table>
            <div className="mt-3 flex flex-col items-end gap-1 text-sm">
              <div>
                <span className="text-ink-500">Rent and recurring charges, {doc.totals.periodSuffix}: </span>
                <span className="font-medium tabular-nums">{doc.totals.perPeriodLabel}</span>
              </div>
              <div>
                <span className="text-ink-500">Payable at commencement: </span>
                <span className="text-base font-semibold tabular-nums">{doc.totals.commencementLabel}</span>
              </div>
            </div>

            <h3 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-ink-500">Conditions</h3>
            <ol className="list-decimal space-y-2 pl-5 text-sm leading-relaxed text-ink-800">
              {doc.conditions.map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </ol>

            {doc.notes ? (
              <>
                <h3 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-ink-500">
                  Further terms recorded by the agency
                </h3>
                <p className="text-sm leading-relaxed text-ink-800">{doc.notes}</p>
              </>
            ) : null}
          </Card>
        </div>

        <div className="grid content-start gap-5">
          <Card>
            <CardTitle>State</CardTitle>
            <div className="mb-4">
              <StatusBadge status={data.status} />
            </div>
            <dl className="grid gap-2">
              <Detail label="Tenancy">
                <Link className="link" to={`/app/tenancies/${data.leaseId}`}>
                  {data.leaseReference}
                </Link>
              </Detail>
              <Detail label="Raised">{fmt.date(data.issuedOn, { long: true })}</Detail>
              <Detail label="Sent">{data.sentAt ? fmt.dateTime(data.sentAt) : "Not yet"}</Detail>
              <Detail label="Completed">{data.completedAt ? fmt.dateTime(data.completedAt) : "Not yet"}</Detail>
            </dl>
          </Card>

          <Card>
            <CardTitle hint="Two copies of the same agreement carry the same fingerprint. One that does not is not this document.">
              Fingerprint
            </CardTitle>
            <p className="font-mono text-sm tracking-wide" data-testid="agreement-fingerprint">
              {data.fingerprint}
            </p>
            <p className="mt-2 text-xs text-ink-500">
              {data.stampedDigest
                ? "Recorded when this agreement was sent, and printed on every copy."
                : "This is a draft. The fingerprint is recorded when it is sent, and the terms are fixed from that moment."}
            </p>
          </Card>

          {mayAct ? (
            <Card>
              <CardTitle>Ending it</CardTitle>
              {data.status === "draft" ? (
                <>
                  <p className="mb-3 text-sm text-ink-600">
                    Nobody has seen this yet, so it can be discarded outright.
                  </p>
                  <Button variant="danger" onClick={() => discard.mutate(undefined as never)} disabled={discard.isPending}>
                    Discard this draft
                  </Button>
                </>
              ) : data.status === "sent" || data.status === "signed" ? (
                withdrawing ? (
                  <form
                    onSubmit={(event: FormEvent<HTMLFormElement>) => {
                      event.preventDefault();
                      const form = new FormData(event.currentTarget);
                      withdraw.mutate({ reason: String(form.get("reason") ?? "") });
                    }}
                  >
                    <Field label="Why is it being withdrawn?" htmlFor="reason" hint="Shown on the document. Signatures already given are kept.">
                      <Input id="reason" name="reason" placeholder="The tenant withdrew before completion" required />
                    </Field>
                    <div className="mt-3 flex gap-2">
                      <Button type="submit" variant="danger" disabled={withdraw.isPending}>
                        Withdraw
                      </Button>
                      <Button type="button" variant="secondary" onClick={() => setWithdrawing(false)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                ) : (
                  <>
                    <p className="mb-3 text-sm text-ink-600">
                      Withdrawing does not remove anybody's signature. It records that the document no longer stands.
                    </p>
                    <Button variant="secondary" onClick={() => setWithdrawing(true)}>
                      Withdraw this agreement
                    </Button>
                  </>
                )
              ) : (
                <p className="text-sm text-ink-600">This agreement is already closed.</p>
              )}
            </Card>
          ) : null}

          <Card>
            <CardTitle>
              <span className="inline-flex items-center gap-2">
                <IconSignature size={16} /> What a signature here is
              </span>
            </CardTitle>
            <p className="text-sm leading-relaxed text-ink-600">{doc.signatureStatement}</p>
            {/*
              The second statement, printed only where it is true of this document — the same rule
              the PDF follows, and the same sentence, from the same constant in core. The paragraph
              above says every party typed their name in the portal; on an agreement where one of
              them did not, saying only that would be saying something untrue about its own
              signatures. It is deliberately not part of the composed document: that text is inside
              the fingerprint every party signed, and a sentence appearing when somebody uploaded a
              scan would report every signature on the agreement as given against different wording.
            */}
            {data.signing.anyOnPaper ? (
              <p className="mt-3 border-t border-ink-200 pt-3 text-sm leading-relaxed text-ink-600" data-testid="paper-statement">
                {PAPER_SIGNATURE_STATEMENT}
              </p>
            ) : null}
          </Card>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------- raising one from a tenancy */

/**
 * The control that lives on a tenancy's own screen.
 *
 * Exported rather than drawn there, so the one place that knows what raising an agreement involves
 * is this file. It takes a tenancy id and sends a tenancy id — there is deliberately nowhere on it
 * to type an amount, because every figure comes from the tenancy.
 */
export function RaiseAgreement({ leaseId, existing }: { leaseId: string; existing: number }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  /**
   * A guarantor, if this letting is guaranteed.
   *
   * **One, not a list, and that is a deliberate limit on the screen rather than on the endpoint.**
   * `POST /agreements` takes up to six extra parties of any signing role; what an agency asks for
   * on a tenancy is a guarantor, and a control that could add six people in four roles would put
   * the rare case in front of everybody who wanted the common one. A letting with two guarantors
   * is recorded through the endpoint until somebody asks for a second box.
   *
   * Empty is the ordinary answer and must stay effortless: most lettings are not guaranteed, and
   * a required picker here would make every one of them slower to agree.
   */
  const [guarantorId, setGuarantorId] = useState("");

  const raise = useApiMutation({
    /* A guarantor is sent as an extra signing party, which is what one is: somebody who signs the
       agreement and is bound by it, not a field on the tenancy. The list is empty when nobody was
       chosen, so the request is byte for byte the one that was sent before this control existed. */
    mutationFn: () =>
      api.post<{ id: string }>("/agreements", {
        leaseId,
        extraParties: guarantorId ? [{ partyId: guarantorId, role: "guarantor" }] : [],
      }),
    invalidates: [["agreements"], ["lease", leaseId]],
    onSuccess: (created) => navigate(`/app/agreements/${created.id}`),
  });

  if (!open) {
    return (
      <Button variant="secondary" onClick={() => setOpen(true)}>
        {existing ? "Raise another agreement" : "Raise an agreement"}
      </Button>
    );
  }

  return (
    <Card className="mt-4">
      <CardTitle hint="The agency, the landlord and the tenant are named automatically — the landlord from the unit, falling back to the building's owner. Add a guarantor below if this letting is guaranteed.">
        Raise an agreement
      </CardTitle>
      <Refusal error={raise.error} message={raise.errorMessage} />
      <p className="mb-3 text-sm text-ink-600">
        Every figure — the rent, its cycle, the deposit and each charge — is taken from this tenancy, so the agreement
        cannot disagree with it. It is created as a draft; nobody sees it until you send it.
      </p>
      {/*
        The guarantor, above the confirmation rather than below it.

        The checkbox is the last act on this card — it says the terms have been checked — so a
        control underneath it would be a question asked after the answer. The picker searches the
        whole client list rather than only the people already marked as guarantors: somebody
        standing surety for a tenancy is very often already on the list as a tenant elsewhere, and
        a filter would hide them at the moment they are needed.
      */}
      <Field
        label="Guarantor"
        htmlFor="guarantorId"
        hint="Optional. Somebody standing surety, who signs the agreement alongside the tenant."
      >
        <SearchSelect
          id="guarantorId"
          value={guarantorId}
          onChange={setGuarantorId}
          search={searchParties()}
          resolve={resolveParty}
          placeholder="Search the client list…"
          emptyMessage="No client matched."
        />
      </Field>

      <Checkbox
        checked={confirmed}
        onChange={(event) => setConfirmed(event.target.checked)}
        label="I have checked the tenancy's terms"
        hint="Once the agreement is sent, its terms are fixed. Changing them afterwards means raising a replacement."
      />
      <div className="mt-4 flex gap-2">
        <Button onClick={() => raise.mutate(undefined as never)} disabled={!confirmed || raise.isPending}>
          {raise.isPending ? "Raising…" : "Raise the draft"}
        </Button>
        <Button variant="secondary" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}
